// Pops Culture - Master TypeScript Interfaces

export type MissionType = 'project' | 'social' | 'volunteer' | 'sports' | 'gaming' | 'sports_gaming';
export type KidPolicy = 'dads_only' | 'kids_welcome' | 'family_all';

export type ContributionRole = 
  | 'experienced' // Experienced Peer
  | 'helper'      // Capable Pair of Hands
  | 'learner'     // Apprentice / Learner
  | 'supplies'    // Quartermaster
  | 'host';       // Event Host

export interface EnlistedDad {
  userId: string;
  name: string;
  avatarInitials: string;
  role: string;
  roleType: ContributionRole;
  toolPledged?: string;
  enlistedAt: number;
}

export interface Mission {
  id: string;
  type: MissionType;
  title: string;
  description: string;
  datetime: string;
  isTentative?: boolean;
  tentativeDates?: string[]; // Candidate dates when tentative
  street?: string;
  city?: string;
  state?: string;
  zipCode: string; // Required 5-digit zip code
  locationMasked: string; // Neighborhood / general area (shown publicly)
  exactAddress: string;   // Full street address (revealed only upon enlistment)
  kidPolicy: KidPolicy;
  kidPolicyText: string;
  provisions?: string;
  skillsNeeded: string[];
  toolsNeeded: string[];
  host: {
    userId: string;
    name: string;
    avatarInitials: string;
    badgesCount: number;
    yearsDad: number;
  };
  enlisted: EnlistedDad[];
  maxSlots: number;
  status?: 'active' | 'under_review' | 'removed';
  reportedReason?: string;
  reportedBy?: string;
  reportedAt?: number;
  createdAt: number;
}

export interface MaydayBeacon {
  id: string;
  authorId: string;
  authorName: string;
  avatarInitials: string;
  text: string;
  urgency: 'Right now! (Next 20 mins)' | 'Need today (within 2 hrs)' | 'This afternoon';
  area: string;
  reward?: string;
  createdAt: number;
  expiresAt: number;
  helpers: string[]; // List of user IDs or names who clicked "I Can Help"
}

export interface FamilySquadMember {
  gender: string; // e.g. "Son (Leo)", "Daughter (Maya)"
  age: string;    // e.g. "8 yrs"
  interest: string; // e.g. "Little League & Lego builds"
}

export interface DadProfile {
  uid: string;
  email: string;
  name: string; // Authoritative Google OAuth account name (immutable by user)
  displayName?: string; // Optional public display name / nickname
  avatarInitials: string;
  photoURL?: string;
  phoneNumber?: string;
  emailVerified?: boolean;
  oauthProvider?: string;
  oauthUid?: string;
  neighborhood: string;
  yearsAsDad: number;
  beverage: string;
  bio: string;
  skills: string[];
  kids: FamilySquadMember[];
  badgesEarned: string[];
  crewCount: number;
  crew?: string[]; // Array of friended Dad UIDs (mutual Crew)
  crewRequestsSent?: string[]; // UIDs to whom requests were sent
  crewRequestsReceived?: string[]; // UIDs from whom requests were received
  status: 'active' | 'inactive' | 'suspended' | 'banned';
  underReview?: boolean;
  suspensionReason?: string;
  suspendedAt?: number;
  reportedBy?: string;
  createdAt: number;
  lastLoginAt: number;
  onboarded: boolean;
  acceptedTermsVersion?: string;
  acceptedTermsAt?: number;
  acceptedMediaRelease?: boolean;
  acceptedMediaReleaseAt?: number;
}

export function getDadDisplayName(profile?: { name?: string; displayName?: string } | null): string {
  if (!profile) return 'Neighbor Dad';
  if (profile.displayName && profile.displayName.trim()) {
    return profile.displayName.trim();
  }
  return profile.name || 'Neighbor Dad';
}

export type ConcernCategory = 'inappropriate' | 'unsafe' | 'spam' | 'other';

export interface ConcernReport {
  id: string;
  type: 'user' | 'event';
  targetId: string; // userId or eventId
  targetNameOrTitle: string;
  reporterUid: string;
  reporterEmail: string;
  category: ConcernCategory;
  categoryLabel: string;
  message?: string;
  status: 'pending' | 'resolved_violation' | 'resolved_cleared' | 'dismissed';
  createdAt: number;
  resolvedAt?: number;
  resolvedBy?: string;
  adminNotes?: string;
}

export interface BannedEmail {
  email: string;
  bannedAt: number;
  bannedBy: string;
  reason: string;
  reportId?: string;
}

export interface TermsSection {
  title: string;
  content: string;
}

export interface TermsConfig {
  version: string;
  lastUpdated: string;
  title: string;
  summary: string;
  sections: TermsSection[];
  updatedAt: number;
}

export type BadgeCategory = 'Universal' | 'Projects' | 'Socials' | 'Volunteering' | 'Sports' | 'Gaming';
export type BadgeActionType = 'first_rsvp' | 'attend' | 'host';

export interface BadgeDefinition {
  id: string;
  name: string;
  category: BadgeCategory;
  eventType?: MissionType;
  actionType: BadgeActionType;
  threshold?: number;
  icon: string;
  desc: string;
  earned?: boolean;
  progress?: string;
}

